/* Stellar navigation v2.6
   Desktop (1000px and up): every menu opens as its own layer under the bar with one structure:
   three questions far left (responses load beneath the question, menu height fixed per menu)
   and three navigation columns to the right. The far-right menu icon opens the concierge:
   customer type, one qualifying question, name, email (required) and phone, then the right door.
   Every concierge lead goes through stellar.submitForm (warehouse + Klaviyo) like every other form.
   Phones and tablets keep the drawer (dialog) exactly as before. Search keeps the dialog on every size. */
(() => {
  'use strict';
  if (window.stellarNavigationLoaded) return;
  window.stellarNavigationLoaded = true;

  const PANEL_GROUPS = ['menu', 'spirits', 'experiences', 'recipes', 'events', 'trade', 'stellar'];
  const MOBILE_GROUPS = ['spirits', 'experiences', 'recipes', 'events', 'trade', 'stellar'];

  /* ------------------------------------------------------------------ */
  /* Desktop menu content. Paths resolve to the links already rendered in */
  /* the drawer, so member and guest rules carry over.                    */
  /* ------------------------------------------------------------------ */
  const SIGNIN = '/customer_authentication/login?return_to=%2Fpages%2Fmy-room';
  const REGIONS = ['California', 'Elsewhere in the USA', 'Canada', 'Mexico', 'Another country'];

  const MENUS = {
    spirits: {
      label: 'Spirits & wine',
      questions: [
        { q: 'Seeking something new?', title: 'Where shall we begin?', choices: [
          { label: 'Explore Mexico', visit: '/collections/origin-mexico', title: 'Where in Mexico?', choices: [
            { label: 'Sonora', visit: '/collections/estaca-1', title: 'Explore Estaca', links: [['/collections/estaca-1', 'Visit Estaca'], ['/collections/bacanora', 'Explore bacanora']] },
            { label: 'Los Altos', visit: '/collections/comisario', title: 'Explore Comisario', links: [['/collections/comisario', 'Visit Comisario'], ['/collections/tequila', 'Explore tequila']] } ] },
          { label: 'Explore the USA', visit: '/collections/origin-usa', title: 'Explore the US collection', links: [['/collections/origin-usa', 'View the US collection']] } ] },
        { q: 'Looking for something specific?', search: true, title: 'Which spirit or house?', placeholder: 'Try tequila, gin or Comisario' },
        { q: 'Would you like a suggestion?', title: 'What are you drawn to?', choices: [
          { label: 'Agave', title: 'Two agave paths to explore', note: 'A starting point based on your choice.', links: [['/collections/tequila', 'Explore tequila'], ['/collections/bacanora', 'Discover bacanora']] },
          { label: 'Whiskey', title: 'Explore the whiskey collection', note: 'A starting point based on your choice.', links: [['/collections/whiskey', 'Explore whiskey'], ['/collections/bourbon', 'Explore bourbon']] },
          { label: 'Gin', title: 'Explore the gin collection', note: 'A starting point based on your choice.', links: [['/collections/gin', 'Discover gin']] } ] } ],
      nav: [
        { title: 'Find your pour', links: [['/collections/tequila', 'Tequila'], ['/collections/bacanora', 'Bacanora'], ['/collections/whiskey', 'Whiskey'], ['/collections/bourbon', 'Bourbon'], ['/collections/gin', 'Gin'], ['/collections/flavored', 'Flavored spirits'], ['/collections/all', 'Explore the cabinet']] },
        { title: 'By house', links: [['/collections/comisario', 'Comisario'], ['/collections/estaca-1', 'Estaca'], ['/collections/woody-creek', 'Woody Creek'], ['/collections/dill-ish', 'Dill-ish'], ['/collections/pilots-legacy-spirits', 'Pilots Legacy Spirits']] },
        { title: 'Explore further', links: [['/collections/agave-spirits', 'Agave spirits'], ['/collections/aged-spirits', 'Aged spirits'], ['/collections/origin-mexico', 'Mexico'], ['/collections/origin-usa', 'USA'], ['/collections', 'Collections'], ['/collections/all?view=brand-merchandise', 'Brand merchandise'], ['/pages/locations', 'Find a pour near you']] } ]
    },
    experiences: {
      label: 'Savor Agave',
      questions: [
        { q: 'Want to bring Savor Agave to your world?', title: 'Choose your route', choices: [
          { label: 'Host at your venue', visit: '/pages/savor-agave-host', title: 'Host at your venue', links: [['/pages/savor-agave-host', 'Explore this route'], ['/pages/contact', 'Speak with Stellar']] },
          { label: 'Become an ambassador', visit: '/pages/savor-agave-ambassadors', title: 'Become an ambassador', links: [['/pages/savor-agave-ambassadors', 'Explore this route'], ['/pages/contact', 'Speak with Stellar']] },
          { label: 'For distributors', visit: '/pages/distributor', title: 'For distributors', links: [['/pages/distributor', 'Explore this route'], ['/pages/contact', 'Speak with Stellar']] },
          { label: 'For agave producers', visit: '/pages/producers', title: 'For agave producers', links: [['/pages/producers', 'Explore this route'], ['/pages/contact', 'Speak with Stellar']] } ] },
        { q: 'Curious what an evening is like?', title: 'The experience', links: [['/pages/introducing-savor-agave', 'Discover the program'], ['/pages/savor-agave-experience', 'Plan your experience'], ['/pages/savor-agave-academy', 'The Academy']] },
        { q: 'Looking for what comes next?', title: 'Continue your experience', links: [['/blogs/events', 'Upcoming events'], ['/blogs/recipes', 'Recipes & pairings'], ['/pages/contact', 'Speak with Stellar']] } ],
      nav: [
        { title: 'Experience Savor Agave', links: [['/pages/savor-agave-academy', 'The Academy'], ['/pages/introducing-savor-agave', 'Discover the program'], ['/pages/savor-agave-experience', 'Plan your experience'], ['/collections/agave-spirits', 'Explore agave spirits']] },
        { title: 'Bring it to your world', links: [['/pages/savor-agave-host', 'Host at your venue'], ['/pages/savor-agave-ambassadors', 'Become an ambassador'], ['/pages/distributor', 'For distributors'], ['/pages/producers', 'For agave producers']] },
        { title: 'Continue your experience', links: [['/blogs/events', 'Upcoming events'], ['/blogs/recipes', 'Recipes & pairings'], ['/pages/contact', 'Speak with Stellar']] } ]
    },
    recipes: {
      label: 'Recipes',
      questions: [
        { q: 'Looking for a recipe?', search: true, title: 'Find a recipe', placeholder: 'Try margarita, brunch or bourbon' },
        { q: 'Making an occasion?', title: 'What is the occasion?', choices: [
          { label: 'Date night', title: 'Date night', links: [['/blogs/recipes?occasion=date-night#library', 'Browse date night recipes']] },
          { label: 'Dinner party', title: 'Dinner party', links: [['/blogs/recipes?occasion=dinner-party#library', 'Browse dinner party recipes']] },
          { label: 'Brunch', title: 'Brunch', links: [['/blogs/recipes?occasion=sunday-brunch#library', 'Browse brunch recipes']] },
          { label: 'For a crowd', title: 'For a crowd', links: [['/blogs/recipes?occasion=a-crowd#library', 'Browse recipes for a crowd'], ['/pages/event-host-apply', 'Host an event']] } ] },
        { q: 'Want to build your bar?', title: 'Your bar & service', links: [['/blogs/recipes#yourbar', 'Your bar'], ['/blogs/recipes#learn', 'Learn the craft'], ['/blogs/recipes?occasion=a-crowd#library', 'Scale recipes for a crowd']] } ],
      nav: [
        { title: 'Find a recipe', links: [['/blogs/recipes#library', 'All recipes'], ['/blogs/recipes#bar', 'Ask the bar'], ['/blogs/recipes#occasions', 'By occasion'], ['/blogs/recipes#learn', 'Learn the craft'], ['/blogs/recipes#wheel', 'Explore flavors']] },
        { title: 'Make the occasion', links: [['/blogs/recipes?occasion=date-night#library', 'Date night'], ['/blogs/recipes?occasion=dinner-party#library', 'Dinner party'], ['/blogs/recipes?occasion=sunday-brunch#library', 'Brunch'], ['/blogs/recipes?occasion=a-crowd#library', 'For a crowd']] },
        { title: 'Your bar & service', links: [['/blogs/recipes#yourbar', 'Your bar'], ['/blogs/recipes?occasion=a-crowd#library', 'Scale recipes for a crowd'], ['/blogs/recipes#library', 'Choose a recipe to share'], ['/pages/event-host-apply', 'Host an event']] } ]
    },
    trade: {
      label: 'Trade',
      questions: [
        { q: 'Which partnership fits you?', title: 'Your partnership', choices: [
          { label: 'Distributors & importers', visit: '/pages/distributor', title: 'Distributors & importers', links: [['/pages/distributor', 'Explore your partnership'], ['/pages/trade-account-application', 'Apply for trade access'], ['/pages/contact', 'Contact the team']] },
          { label: 'Bars, restaurants & stores', visit: '/pages/on-off-premise', title: 'Bars, restaurants & stores', links: [['/pages/on-off-premise', 'Explore your partnership'], ['/pages/trade-account-application', 'Apply for trade access'], ['/pages/contact', 'Contact the team']] },
          { label: 'Spirit & wine producers', visit: '/pages/producers', title: 'Spirit & wine producers', links: [['/pages/producers', 'Explore your partnership'], ['/pages/trade-account-application', 'Apply for trade access'], ['/pages/contact', 'Contact the team']] } ] },
        { q: 'Already a partner?', title: 'Welcome back', links: [['/pages/my-room', 'My room', 'member'], [SIGNIN, 'Join / Sign in', 'guest'], ['/pages/getting-started-with-stellar', 'Getting started']] },
        { q: 'Want to grow with Stellar?', title: 'Grow with Stellar', links: [['/pages/region-manager-application', 'Represent Stellar'], ['/pages/introducing-savor-agave', 'Explore Savor Agave'], ['/pages/event-host-apply', 'Host an event']] } ],
      nav: [
        { title: 'Your partnership', links: [['/pages/distributor', 'Distributors & importers'], ['/pages/on-off-premise', 'Bars, restaurants & stores'], ['/pages/producers', 'Spirit & wine producers'], ['/pages/trade-account-application', 'Apply for trade access']] },
        { title: 'Already a partner?', links: [['/pages/my-room', 'My room', 'member'], ['/pages/orders', 'Your orders', 'member'], [SIGNIN, 'Join / Sign in', 'guest'], ['/pages/getting-started-with-stellar', 'Getting started'], ['/pages/contact', 'Contact the team']] },
        { title: 'Grow with Stellar', links: [['/pages/region-manager-application', 'Represent Stellar'], ['/pages/introducing-savor-agave', 'Explore Savor Agave'], ['/pages/event-host-apply', 'Host an event']] } ]
    },
    locations: {
      label: 'Find a pour',
      questions: [
        { q: 'Where would you like to taste?', title: 'Choose a place', choices: [
          { label: 'A bar or store near you', visit: '/pages/locations', title: 'Near you', links: [['/pages/locations', 'Find a pour near you'], ['/pages/contact', 'Ask Stellar where to go']] },
          { label: 'An event', visit: '/blogs/events', title: 'Events', links: [['/blogs/events', 'Explore events'], ['/pages/savor-agave-experience', 'Plan a Savor experience']] },
          { label: 'A Savor Agave evening', visit: '/pages/savor-agave-experience', title: 'Savor Agave', links: [['/pages/savor-agave-experience', 'Plan a Savor experience'], ['/pages/introducing-savor-agave', 'Discover the program']] } ] },
        { q: 'Want to host a pour?', title: 'Host a pour', links: [['/pages/savor-agave-host', 'Host at your venue'], ['/pages/event-host-apply', 'Apply to host an event'], ['/pages/contact', 'Speak with Stellar']] },
        { q: 'Nothing near you yet?', title: 'Bring it home', links: [['/collections/all', 'Explore the cabinet'], ['/collections/tequila', 'Tequila'], ['/collections/bacanora', 'Bacanora'], ['/pages/contact', 'Ask for a pour near you']] } ],
      nav: [
        { title: 'Find a pour', links: [['/pages/locations', 'Find a pour near you'], ['/blogs/events', 'Explore events'], ['/pages/savor-agave-experience', 'Plan a Savor experience']] },
        { title: 'Host a pour', links: [['/pages/savor-agave-host', 'Host at your venue'], ['/pages/event-host-apply', 'Apply to host an event'], ['/pages/contact', 'Speak with Stellar']] },
        { title: 'Bring it home', links: [['/collections/all', 'Explore the cabinet'], ['/collections/tequila', 'Tequila'], ['/collections/bacanora', 'Bacanora'], ['/collections/all?view=brand-merchandise', 'Brand merchandise']] } ]
    },
    stellar: {
      label: 'Your Stellar',
      questions: [
        { q: 'Already have a room?', title: 'Your room', links: [['/pages/my-room', 'My room', 'member'], ['/pages/orders', 'Your orders', 'member'], [SIGNIN, 'Join / Sign in', 'guest'], ['/pages/getting-started-with-stellar', 'Getting started', 'guest']] },
        { q: 'New to Stellar?', title: 'Start here', links: [['/pages/our-story', 'Our story'], ['/pages/introducing-savor-agave', 'Discover Savor Agave'], ['/collections/all', 'Explore the cabinet']] },
        { q: 'Need to reach the team?', title: 'Reach us', links: [['/pages/contact', 'Contact Stellar'], ['/pages/team', 'Meet the team'], ['/blogs/news', 'News']] } ],
      nav: [
        { title: 'Your account', account: true },
        { title: 'The world of Stellar', links: [['/pages/our-story', 'Our story'], ['/pages/team', 'Meet the team'], ['/blogs/brand', 'House stories'], ['/blogs/stellar-sips-magazine', 'Stellar Sips magazine'], ['/blogs/awards', 'Awards'], ['/blogs/news', 'News'], ['/pages/contact', 'Contact Stellar']] },
        { title: 'Explore & connect', links: [['/collections/all', 'Spirits & wine'], ['/pages/introducing-savor-agave', 'Savor Agave'], ['/blogs/recipes', 'Recipes'], ['/blogs/events', 'Events'], ['/collections/all?view=brand-merchandise', 'Brand merchandise'], ['/pages/locations', 'Find a pour']] } ]
    }
  };

  /* Concierge: who they are, one question, then the door. */
  const CONCIERGE_TYPES = [
    { label: 'Sip and discover', key: 'guest', q2: 'What do you reach for?', opts: ['Tequila', 'Bacanora', 'Whiskey & bourbon', 'Gin', 'Surprise me'],
      routes: a => ({
        'Tequila': [['/collections/comisario', 'Comisario'], ['/collections/tequila', 'Explore tequila'], ['/blogs/recipes', 'Recipes']],
        'Bacanora': [['/collections/estaca-1', 'Estaca'], ['/collections/bacanora', 'Explore bacanora'], ['/blogs/recipes', 'Recipes']],
        'Whiskey & bourbon': [['/collections/whiskey', 'Whiskey'], ['/collections/bourbon', 'Bourbon'], ['/blogs/recipes', 'Recipes']],
        'Gin': [['/collections/gin', 'Gin'], ['/collections/dill-ish', 'Dill-ish'], ['/blogs/recipes', 'Recipes']]
      }[a] || [['/collections/all', 'Explore the cabinet'], ['/collections/agave-spirits', 'Agave spirits'], ['/collections/aged-spirits', 'Aged spirits']]),
      also: [['/pages/locations', 'Find a pour near you'], ['/blogs/events', 'Upcoming events'], ['/pages/my-room', 'Open your room', 'member'], [SIGNIN, 'Open your room', 'guest']] },
    { label: 'Run a bar, restaurant or store', key: 'onoff', q2: 'Where do you pour?', opts: REGIONS,
      routes: () => [['/pages/on-off-premise', 'Bars, restaurants & stores'], ['/pages/trade-account-application', 'Apply for trade access'], ['/pages/getting-started-with-stellar', 'Getting started']],
      also: [['/pages/savor-agave-host', 'Host Savor Agave'], ['/pages/contact', 'Contact the team'], ['/pages/my-room', 'Open your room', 'member'], [SIGNIN, 'Open your room', 'guest']] },
    { label: 'Distribute or import', key: 'distributor', q2: 'Where do you operate?', opts: REGIONS,
      routes: () => [['/pages/distributor', 'Distributors & importers'], ['/pages/trade-account-application', 'Apply for trade access'], ['/pages/getting-started-with-stellar', 'Getting started']],
      also: [['/pages/team', 'Meet the team'], ['/pages/contact', 'Contact the team'], ['/pages/my-room', 'Open your room', 'member'], [SIGNIN, 'Open your room', 'guest']] },
    { label: 'Produce spirits or wine', key: 'producer', q2: 'What do you make?', opts: ['Agave spirits', 'Whiskey or bourbon', 'Gin', 'Wine', 'Something else'],
      routes: () => [['/pages/producers', 'Spirit & wine producers'], ['/pages/trade-account-application', 'Apply for trade access'], ['/pages/contact', 'Contact the team']],
      also: [['/pages/our-story', 'Our story'], ['/blogs/brand', 'House stories'], ['/pages/my-room', 'Open your room', 'member'], [SIGNIN, 'Open your room', 'guest']] },
    { label: 'Host events or pour as an ambassador', key: 'host', q2: 'What would you like to do?', opts: ['Host a Savor Agave evening', 'Become an ambassador', 'Host a brand event'],
      routes: a => ({
        'Host a Savor Agave evening': [['/pages/savor-agave-host', 'Host at your venue'], ['/pages/introducing-savor-agave', 'Discover the program'], ['/pages/contact', 'Speak with Stellar']],
        'Become an ambassador': [['/pages/savor-agave-ambassadors', 'Become an ambassador'], ['/pages/savor-agave-academy', 'The Academy'], ['/pages/contact', 'Speak with Stellar']],
        'Host a brand event': [['/pages/event-host-apply', 'Apply to host an event'], ['/blogs/events', 'Explore events'], ['/pages/contact', 'Speak with Stellar']]
      }[a] || [['/pages/savor-agave-host', 'Host at your venue'], ['/pages/event-host-apply', 'Apply to host an event'], ['/pages/savor-agave-ambassadors', 'Become an ambassador']]),
      also: [['/blogs/recipes?occasion=a-crowd#library', 'Recipes for a crowd'], ['/blogs/events', 'Upcoming events'], ['/pages/my-room', 'Open your room', 'member'], [SIGNIN, 'Open your room', 'guest']] }
  ];
  const CONCIERGE_DEFAULT_ROUTE = [['/collections/all', 'Explore the cabinet'], ['/pages/locations', 'Find a pour near you'], ['/pages/my-room', 'My room', 'member'], [SIGNIN, 'Join / Sign in', 'guest']];

  /* ------------------------------------------------------------------ */
  /* Desktop layers                                                      */
  /* ------------------------------------------------------------------ */
  function createDesktopMenus(root, dialog, listen, mobile, fine) {
    const header = root.querySelector('#sn-header');
    const layer = document.createElement('div');
    layer.className = 'sn-mega-layer';
    header.after(layer);

    const PLUS = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8h10M8 3v10"></path></svg>';
    const ARROW = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 13 13 3M3 3h10v10"></path></svg>';
    const svg = markup => { const t = document.createElement('template'); t.innerHTML = markup.trim(); return t.content.firstChild; };

    const megas = new Map();
    let active = null;
    let activeTrigger = null;
    let hoverTimer = null;

    function sourceAnchor(path) {
      const list = dialog.querySelectorAll('a[href]');
      for (const a of list) if (a.getAttribute('href') === path) return a;
      return null;
    }
    function signinAnchor() {
      const list = root.querySelectorAll('a[href]');
      for (const a of list) if (/customer_authentication|\/account\/login|signin|sign-in/i.test(a.getAttribute('href') || '') && /join|sign in/i.test(a.textContent)) return a;
      return null;
    }
    function row(path, label, audience) {
      let src = path === SIGNIN ? signinAnchor() : sourceAnchor(path);
      const a = document.createElement('a');
      a.className = 'sn-row sn-mega-row';
      a.href = src ? src.getAttribute('href') : path;
      if (src && src.target) a.target = src.target;
      a.append(document.createTextNode(label), svg(ARROW));
      if (audience === 'member') a.classList.add('sn-member-only');
      if (audience === 'guest') a.classList.add('sn-guest-only');
      return a;
    }
    function col(n, title) {
      const c = document.createElement('div');
      c.className = 'sn-mega-col';
      c.dataset.col = n;
      const h = document.createElement('h3');
      h.textContent = title;
      c.append(h);
      return c;
    }
    function note(text) { const p = document.createElement('p'); p.className = 'sn-mega-note'; p.textContent = text; return p; }
    function accountRows() {
      const out = [];
      dialog.querySelectorAll('[data-sn-panel="account"] a.sn-row').forEach(a => {
        const c = a.cloneNode(true);
        c.classList.add('sn-mega-row');
        out.push(c);
      });
      return out;
    }
    function settle(button, action) {
      /* Hover acts once the pointer settles; click and keyboard act at once. */
      let timer = null;
      listen(button, 'pointerenter', e => { if (!fine.matches || e.pointerType === 'touch') return; clearTimeout(timer); timer = setTimeout(action, 180); });
      listen(button, 'pointerleave', () => clearTimeout(timer));
      listen(button, 'click', () => { clearTimeout(timer); action(); });
    }

    function buildMenu(key) {
      const data = MENUS[key];
      const mega = document.createElement('section');
      mega.className = 'sn-mega';
      mega.dataset.snMega = key;
      mega.setAttribute('aria-label', data.label);
      mega.hidden = true;
      const inner = document.createElement('div');
      inner.className = 'sn-mega-inner';
      const questions = document.createElement('div');
      questions.className = 'sn-mega-questions';
      const board = document.createElement('div');
      board.className = 'sn-mega-board';
      inner.append(questions, board);
      mega.append(inner);

      data.nav.forEach((group, i) => {
        const c = col(i + 1, group.title);
        if (group.account) accountRows().forEach(r => c.append(r));
        else group.links.forEach(([p, l, who]) => c.append(row(p, l, who)));
        board.append(c);
      });

      const source = dialog.querySelector('[data-sn-rollover-panel="' + key + '"]') || dialog;

      function renderStep(region, node, trail) {
        region.replaceChildren();
        const stage = document.createElement('div');
        stage.className = 'sn-mega-stage';
        region.append(stage);
        if (trail.length) {
          const t = document.createElement('div');
          t.className = 'sn-mega-trail';
          const back = document.createElement('button');
          back.type = 'button';
          back.append(svg(PLUS), document.createTextNode('Back'));
          listen(back, 'click', () => { const prev = trail[trail.length - 1]; renderStep(region, prev.node, trail.slice(0, -1)); });
          const label = document.createElement('span');
          label.textContent = trail.map(x => x.label).join(' · ');
          t.append(back, label);
          stage.append(t);
        }
        const h = document.createElement('h3');
        h.textContent = node.title;
        stage.append(h);
        if (node.note) stage.append(note(node.note));
        if (node.search) {
          const input = document.createElement('input');
          input.type = 'search';
          input.className = 'sn-mega-search';
          input.placeholder = node.placeholder || '';
          input.setAttribute('aria-label', node.title);
          input.autocomplete = 'off';
          const results = document.createElement('div');
          results.setAttribute('aria-live', 'polite');
          stage.append(input, results);
          const seen = new Map();
          source.querySelectorAll('a.sn-row[href]').forEach(a => { if (!seen.has(a.href)) seen.set(a.href, a); });
          const entries = [...seen.values()];
          listen(input, 'input', () => {
            results.replaceChildren();
            const q = input.value.trim().toLowerCase();
            if (!q) return;
            const found = entries.filter(a => a.textContent.toLowerCase().includes(q));
            found.slice(0, 3).forEach(a => { const c = a.cloneNode(true); c.className = 'sn-row sn-mega-row'; results.append(c); });
            if (!found.length) results.append(note('No match. Try another word.'));
          });
          setTimeout(() => { if (!region.hidden && !mega.hidden) input.focus({ preventScroll: true }); }, 60);
        }
        if (node.choices) node.choices.forEach(choice => {
          const r = document.createElement('div');
          r.className = 'sn-mega-choice';
          const b = document.createElement('button');
          b.type = 'button';
          b.textContent = choice.label;
          b.setAttribute('aria-expanded', 'false');
          r.append(b);
          if (choice.visit) {
            const v = row(choice.visit, 'Visit');
            v.className = 'sn-mega-visit';
            v.setAttribute('aria-label', 'Visit ' + choice.label);
            r.append(v);
          }
          settle(b, () => renderStep(region, choice, trail.concat([{ node, label: choice.label }])));
          stage.append(r);
        });
        if (node.links) node.links.forEach(([p, l, who]) => stage.append(row(p, l, who)));
      }

      data.questions.forEach((node, index) => {
        const path = document.createElement('section');
        path.className = 'sn-mega-path';
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'sn-mega-question';
        b.append(document.createTextNode(node.q), svg(PLUS));
        b.setAttribute('aria-expanded', 'false');
        const region = document.createElement('div');
        region.className = 'sn-mega-response';
        region.id = 'sn-mega-' + key + '-' + index;
        region.hidden = true;
        b.setAttribute('aria-controls', region.id);
        listen(b, 'click', () => {
          const open = b.getAttribute('aria-expanded') === 'true';
          resetMenu(mega);
          if (!open) { b.setAttribute('aria-expanded', 'true'); region.hidden = false; renderStep(region, node, []); }
        });
        path.append(b, region);
        questions.append(path);
      });

      layer.append(mega);
      sizeMenu(mega, data);
      return mega;
    }

    function resetMenu(mega) {
      mega.querySelectorAll('.sn-mega-path').forEach(p => {
        p.querySelector('.sn-mega-question').setAttribute('aria-expanded', 'false');
        p.querySelector('.sn-mega-response').hidden = true;
      });
    }

    /* The menu never changes height: it reserves the taller of its columns and its tallest response. */
    function sizeMenu(mega, data) {
      const inner = mega.querySelector('.sn-mega-inner');
      const questions = mega.querySelector('.sn-mega-questions');
      const board = mega.querySelector('.sn-mega-board');
      const wasHidden = mega.hidden;
      mega.hidden = false;
      mega.style.visibility = 'hidden';
      inner.style.setProperty('--sn-mega-min', '0px');
      let max = board.getBoundingClientRect().height;
      [...mega.querySelectorAll('.sn-mega-question')].forEach((b, i) => {
        b.click();
        const region = mega.querySelectorAll('.sn-mega-response')[i];
        const measure = () => { let h = questions.getBoundingClientRect().height; if (data.questions[i].search) h += 90; max = Math.max(max, h); };
        measure();
        let c, guard = 0;
        while ((c = region.querySelector('.sn-mega-choice > button')) && guard++ < 6) { c.click(); measure(); }
        b.click();
      });
      resetMenu(mega);
      inner.style.setProperty('--sn-mega-min', Math.ceil(max) + 'px');
      mega.style.visibility = '';
      mega.hidden = wasHidden;
    }

    /* ---------------- Concierge ---------------- */
    function buildConcierge() {
      const mega = document.createElement('section');
      mega.className = 'sn-mega sn-concierge';
      mega.dataset.snMega = 'concierge';
      mega.setAttribute('aria-label', 'Your concierge');
      mega.hidden = true;
      const inner = document.createElement('div');
      inner.className = 'sn-mega-inner';
      const left = document.createElement('div');
      left.className = 'sn-mega-questions';
      const board = document.createElement('div');
      board.className = 'sn-mega-board';
      inner.append(left, board);
      mega.append(inner);
      layer.append(mega);

      const state = { type: null, answer: null, name: '', email: '', phone: '' };
      let pendingLead = null;
      const reset = () => { Object.assign(state, { type: null, answer: null, name: '', email: '', phone: '' }); pendingLead = null; };
      const routesNow = () => state.type ? state.type.routes(state.answer) : CONCIERGE_DEFAULT_ROUTE;

      function routeCol() { const c = col(3, 'Your route'); routesNow().forEach(([p, l, who]) => c.append(row(p, l, who))); return c; }
      function summaryCol() {
        const c = col(3, 'You told us');
        [['Who', state.type ? state.type.label : ''], ['Answer', state.answer || ''], ['Email', state.email], ['Phone', state.phone]]
          .filter(x => x[1]).forEach(([k, v]) => {
            const d = document.createElement('div');
            d.className = 'sn-mega-summary';
            const s = document.createElement('small');
            s.textContent = k;
            d.append(s, document.createTextNode(v));
            c.append(d);
          });
        return c;
      }
      function option(label, pressed, onPick) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'sn-mega-option';
        b.append(document.createTextNode(label), svg(PLUS));
        b.setAttribute('aria-pressed', String(pressed));
        listen(b, 'click', onPick);
        return b;
      }
      function submitLead() {
        const first = routesNow()[0];
        const lead = {
          form: 'concierge',
          form_version: '1',
          purpose: 'lead',
          marketing_consent: true,
          email: state.email,
          name: state.name,
          phone: state.phone,
          fields: { who: state.type.label, who_key: state.type.key, answer: state.answer || '', route: first ? first[0] : '' },
          field_labels: { who: 'What brings you to Stellar?', who_key: 'Customer type', answer: state.type.q2, route: 'Door opened' }
        };
        const api = window.stellar && typeof window.stellar.submitForm === 'function' ? window.stellar.submitForm : null;
        pendingLead = api ? api(lead, { owner: mega }).then(() => true, () => false) : Promise.resolve(false);
        return pendingLead;
      }
      function render(step) {
        left.replaceChildren();
        board.replaceChildren();
        const stepLabel = document.createElement('div');
        stepLabel.className = 'sn-mega-step';
        stepLabel.textContent = step < 4 ? 'Step ' + step + ' of 3' : 'Your door is open';
        const q = document.createElement('div');
        q.className = 'sn-mega-ask';
        left.append(stepLabel, q);
        if (step > 1) {
          const t = document.createElement('div');
          t.className = 'sn-mega-trail';
          const back = document.createElement('button');
          back.type = 'button';
          back.append(svg(PLUS), document.createTextNode(step === 4 ? 'Start over' : 'Back'));
          listen(back, 'click', () => { if (step === 4) { reset(); render(1); } else render(step - 1); });
          t.append(back);
          left.append(t);
        }
        if (step === 1) {
          q.textContent = 'What brings you to Stellar?';
          const c = col(1, 'I am here to');
          CONCIERGE_TYPES.forEach(ty => c.append(option(ty.label, state.type === ty, () => { if (state.type !== ty) state.answer = null; state.type = ty; render(2); })));
          const w = col(2, 'Why we ask');
          w.append(note('One minute. We open the right door for you and keep you posted on what matters to you, nothing else.'));
          board.append(c, w, routeCol());
        }
        if (step === 2) {
          q.textContent = state.type.q2;
          const c = col(1, 'Choose one');
          state.type.opts.forEach(o => c.append(option(o, state.answer === o, () => { state.answer = o; render(3); })));
          const w = col(2, 'Why we ask');
          w.append(note('Your answer sets your route: the pages, prices and people that apply to you.'));
          board.append(c, w, routeCol());
        }
        if (step === 3) {
          q.textContent = 'Where can we reach you?';
          const c = col(1, 'Your details');
          const field = (type, placeholder, autocomplete, value) => {
            const i = document.createElement('input');
            i.className = 'sn-mega-field';
            i.type = type; i.placeholder = placeholder; i.autocomplete = autocomplete; i.value = value;
            return i;
          };
          const name = field('text', 'Name', 'name', state.name);
          const email = field('email', 'Email (required)', 'email', state.email);
          email.required = true;
          const phone = field('tel', 'Phone', 'tel', state.phone);
          phone.inputMode = 'tel';
          const go = document.createElement('button');
          go.type = 'button';
          go.className = 'sn-mega-cta';
          go.append(document.createTextNode('Open my door'), svg(ARROW));
          const valid = v => (window.stellar && window.stellar.forms && window.stellar.forms.validEmail) ? window.stellar.forms.validEmail(v) : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
          listen(go, 'click', () => {
            state.name = name.value.trim(); state.email = email.value.trim().toLowerCase(); state.phone = phone.value.trim();
            if (!valid(state.email)) { email.classList.add('sn-invalid'); email.focus(); return; }
            submitLead();
            render(4);
          });
          [name, email, phone].forEach(f => listen(f, 'keydown', e => { if (e.key === 'Enter') { e.preventDefault(); go.click(); } }));
          c.append(name, email, phone, go);
          const w = col(2, 'Why we ask');
          w.append(note('Your email opens your room and lets the desk follow up. Your phone lets us text you about orders and tastings.'));
          board.append(c, w, routeCol());
          setTimeout(() => { if (!mega.hidden) name.focus({ preventScroll: true }); }, 60);
        }
        if (step === 4) {
          q.textContent = (state.name ? state.name + ', your' : 'Your') + ' route is ready.';
          const c = col(1, 'Go to');
          routesNow().forEach(([p, l, who]) => c.append(row(p, l, who)));
          const w = col(2, 'Also for you');
          state.type.also.forEach(([p, l, who]) => w.append(row(p, l, who)));
          const s = summaryCol();
          const saved = note('Saving your details.');
          s.append(saved);
          (pendingLead || Promise.resolve(false)).then(ok => { saved.textContent = ok ? 'Saved. The desk will be in touch.' : 'We could not save your details. Please write to the desk from the contact page.'; });
          board.append(c, w, s);
        }
      }
      render(1);
      /* Fixed height: the taller of step 1 (five options) and step 3 (three fields). */
      mega.hidden = false;
      mega.style.visibility = 'hidden';
      inner.style.setProperty('--sn-mega-min', '0px');
      let h = Math.ceil(Math.max(board.getBoundingClientRect().height, left.getBoundingClientRect().height));
      Object.assign(state, { type: CONCIERGE_TYPES[0], answer: 'Tequila' });
      render(3);
      h = Math.max(h, Math.ceil(board.getBoundingClientRect().height));
      reset();
      render(1);
      inner.style.setProperty('--sn-mega-min', h + 'px');
      mega.style.visibility = '';
      mega.hidden = true;
      mega.snReset = () => { reset(); render(1); };
      return mega;
    }

    function get(key) {
      if (megas.has(key)) return megas.get(key);
      const mega = key === 'concierge' ? buildConcierge() : MENUS[key] ? buildMenu(key) : null;
      if (mega) megas.set(key, mega);
      return mega;
    }
    const keyFor = name => name === 'account' ? 'stellar' : name === 'menu' ? 'concierge' : name;
    const handles = name => !mobile.matches && (name === 'concierge' || name === 'menu' || name === 'account' || !!MENUS[keyFor(name)]);

    function syncTriggers() {
      root.querySelectorAll('[data-sn-open]').forEach(t => {
        const key = keyFor(t.dataset.snOpen);
        if (!handles(t.dataset.snOpen)) return;
        t.setAttribute('aria-expanded', String(!!active && active.dataset.snMega === key));
        if (active && active.dataset.snMega === key) t.setAttribute('aria-controls', 'sn-mega-' + key);
      });
    }

    function open(name, trigger) {
      const key = keyFor(name);
      const mega = get(key);
      if (!mega) return false;
      if (dialog.open) dialog.close();
      if (active && active !== mega) { active.hidden = true; if (active.snReset) active.snReset(); else resetMenu(active); }
      layer.style.top = Math.round(header.getBoundingClientRect().bottom) + 'px';
      mega.id = 'sn-mega-' + key;
      mega.hidden = false;
      active = mega;
      activeTrigger = trigger || null;
      syncTriggers();
      return true;
    }
    function close() {
      if (!active) return;
      const current = active;
      active = null;
      current.hidden = true;
      if (current.snReset) current.snReset(); else resetMenu(current);
      syncTriggers();
      if (activeTrigger && activeTrigger.isConnected && document.activeElement && current.contains(document.activeElement)) activeTrigger.focus({ preventScroll: true });
      activeTrigger = null;
    }
    function hover(name, trigger) {
      clearTimeout(hoverTimer);
      hoverTimer = setTimeout(() => { if (handles(name)) open(name, trigger); }, 180);
    }
    const cancelHover = () => clearTimeout(hoverTimer);

    /* Leave below the layer, press Escape or click elsewhere: the layer closes. */
    listen(document, 'pointermove', e => {
      if (!active || !fine.matches || e.pointerType === 'touch') return;
      if (e.clientY > active.getBoundingClientRect().bottom + 8) close();
    });
    listen(document, 'keydown', e => { if (active && e.key === 'Escape') close(); });
    listen(document, 'pointerdown', e => { if (active && !active.contains(e.target) && !header.contains(e.target)) close(); });
    listen(mobile, 'change', () => { if (mobile.matches) close(); });
    listen(dialog, 'close', syncTriggers);

    function focusFirst() { active?.querySelector('button,a,input')?.focus({ preventScroll: true }); }

    return { open, close, hover, cancelHover, handles, focusFirst, get isOpen() { return !!active; }, destroy() { close(); layer.remove(); } };
  }

  /* ------------------------------------------------------------------ */
  /* The drawer (dialog): unchanged behaviour from v2.5, plus the hooks  */
  /* that send desktop triggers to their layers.                         */
  /* ------------------------------------------------------------------ */
  function init() {
    const root = document.querySelector('.stellar-navigation');
    const dialog = root?.querySelector('#sn-dialog');
    if (!dialog || dialog.dataset.snReady || typeof dialog.showModal !== 'function') return;
    dialog.dataset.snReady = 'true';
    const controller = new AbortController();
    const listen = (target, type, callback, options = {}) => target?.addEventListener(type, callback, { ...options, signal: controller.signal });
    const panels = [...dialog.querySelectorAll('[data-sn-panel]')];
    const mobile = matchMedia('(max-width:999px)');
    const context = dialog.querySelector('[data-sn-context]');
    const back = [...dialog.querySelectorAll('[data-sn-back]')];
    const form = root.querySelector('#sn-search-form');
    const input = root.querySelector('#sn-query');
    const clear = dialog.querySelector('[data-sn-clear]');
    const results = root.querySelector('#sn-results');
    const status = root.querySelector('#sn-search-status');
    const tabs = [...dialog.querySelectorAll('[data-sn-tab]')];
    const tabPanels = [...dialog.querySelectorAll('[data-sn-tab-panel]')];
    const tabList = dialog.querySelector('[data-sn-tabs]');
    const scroller = dialog.querySelector('.sn-panel-scroll') || dialog;
    const scrollPositions = new Map();
    const labels = { menu: 'Explore Stellar', spirits: 'Spirits & wine', experiences: 'Experiences', trade: 'Trade', search: 'Search', account: 'Account' };
    let active = '';
    let activePanel = null;
    let opener = null;
    let menuReturnFocus = null;
    let scrollState = null;
    let activeTab = 'categories';
    const index = [];
    const seen = new Set();
    root.querySelectorAll('.sn-row[href],.sn-cta[href],.sn-atmosphere-action[href]').forEach(anchor => {
      const url = anchor.getAttribute('href');
      const label = anchor.textContent.trim();
      if (!url || !label || seen.has(url)) return;
      seen.add(url);
      const group = anchor.closest('[data-sn-rollover-panel]')?.getAttribute('aria-labelledby');
      const groupLabel = group ? root.querySelector('#' + group)?.textContent.trim() : 'Explore Stellar';
      index.push({ label, url, group: groupLabel || 'Explore Stellar', keywords: label });
    });
    if (form) form.method = 'get';
    if (input) input.name = 'q';
    if (status) { status.setAttribute('role', 'status'); status.setAttribute('aria-live', 'polite'); status.setAttribute('aria-atomic', 'true'); }
    if (context) { context.id ||= 'sn-dialog-context'; dialog.setAttribute('aria-labelledby', context.id); }
    dialog.setAttribute('aria-modal', 'true');
    if (!dialog.hasAttribute('tabindex')) dialog.tabIndex = -1;

    const visible = node => !!node && node.getClientRects().length > 0 && getComputedStyle(node).visibility !== 'hidden';
    const normalize = text => String(text).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
    const panelKey = panel => (panel.dataset.snView || 'shared') + ':' + panel.dataset.snPanel;

    function findPanel(name) {
      if (PANEL_GROUPS.includes(name)) name = 'navigation';
      const view = mobile.matches ? 'mobile' : 'desktop';
      return panels.find(panel => panel.dataset.snPanel === name && panel.dataset.snView === view)
        || panels.find(panel => panel.dataset.snPanel === name && (!panel.dataset.snView || panel.dataset.snView === 'shared'));
    }
    function focusPanel(preferred) {
      const panel = activePanel || findPanel(active);
      const first = active === 'search' ? input
        : active === 'navigation' && !mobile.matches ? panel?.querySelector('[data-sn-rollover][aria-selected=true]')
        : [...panel?.querySelectorAll('h2,h1') || []].find(visible) || panel;
      if (first && first !== input && first.getAttribute('role') !== 'tab') { first.tabIndex = -1; first.classList.add('sn-focus-anchor'); }
      (visible(preferred) ? preferred : first || dialog.querySelector('[data-sn-close]') || dialog).focus({ preventScroll: true });
    }
    function updateTriggers() {
      root.querySelectorAll('[data-sn-open]').forEach(trigger => {
        if (desktop && desktop.handles(trigger.dataset.snOpen) && !dialog.open) return;
        trigger.setAttribute('aria-controls', dialog.id);
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-expanded', String(dialog.open && (trigger === opener || trigger.dataset.snOpen === active)));
      });
    }
    function updateTabs() {
      if (tabList) {
        tabList.hidden = !mobile.matches;
        if (mobile.matches) { tabList.setAttribute('role', 'tablist'); tabList.setAttribute('aria-label', 'Browse spirits'); }
        else tabList.removeAttribute('role');
      }
      tabs.forEach(tab => {
        const key = tab.dataset.snTab;
        const panel = tabPanels.find(item => item.dataset.snTabPanel === key);
        tab.id ||= 'sn-tab-' + key;
        if (panel) { panel.id ||= 'sn-tab-panel-' + key; tab.setAttribute('aria-controls', panel.id); }
        tab.hidden = !mobile.matches;
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-selected', String(key === activeTab));
        tab.tabIndex = key === activeTab ? 0 : -1;
      });
      tabPanels.forEach(panel => {
        panel.hidden = mobile.matches && panel.dataset.snTabPanel !== activeTab;
        if (mobile.matches) { panel.setAttribute('role', 'tabpanel'); panel.setAttribute('aria-labelledby', 'sn-tab-' + panel.dataset.snTabPanel); }
        else { panel.removeAttribute('role'); panel.removeAttribute('aria-labelledby'); }
      });
    }
    function updateLayout() {
      if (dialog.open && active && findPanel(active) !== activePanel) { setPanel(active); focusPanel(); return; }
      const mega = !mobile.matches && active === 'navigation';
      dialog.classList.toggle('sn-mega', mega);
      dialog.classList.toggle('sn-mobile', !mega);
      updateTabs();
      if (dialog.open && !visible(document.activeElement)) focusPanel();
    }
    function setPanel(name, restorePosition = false) {
      const requestedGroup = name;
      if (PANEL_GROUPS.includes(name)) name = 'navigation';
      const selected = findPanel(name);
      if (!selected) return false;
      if (activePanel) scrollPositions.set(panelKey(activePanel), scroller.scrollTop);
      active = name;
      activePanel = selected;
      panels.forEach(panel => { panel.hidden = panel !== selected; panel.classList.toggle('sn-active', panel === selected); });
      if (context) context.textContent = selected.dataset.snLabel || labels[name] || name;
      back.forEach(button => button.hidden = name === 'navigation');
      dialog.dataset.snActive = name;
      updateLayout();
      updateTriggers();
      if (name === 'search') renderSearch();
      if (!mobile.matches && name === 'navigation') showRollover(['menu', 'navigation'].includes(requestedGroup) ? 'spirits' : requestedGroup);
      if (mobile.matches && name === 'navigation' && MOBILE_GROUPS.includes(requestedGroup)) selected.querySelectorAll('[data-sn-group]').forEach(group => group.open = group.dataset.snGroup === requestedGroup);
      scroller.scrollTop = restorePosition && scrollPositions.get(panelKey(selected)) || 0;
      return true;
    }
    function lockScroll() {
      const html = document.documentElement, body = document.body;
      scrollState = { x: scrollX, y: scrollY, htmlOverflow: html.style.overflow, position: body.style.position, top: body.style.top, width: body.style.width, overflow: body.style.overflow };
      html.style.overflow = 'hidden';
      body.style.position = 'fixed';
      body.style.top = -scrollState.y + 'px';
      body.style.width = '100%';
      body.style.overflow = 'hidden';
    }
    function restoreScroll() {
      if (!scrollState) return;
      const html = document.documentElement, body = document.body, saved = scrollState;
      html.style.overflow = saved.htmlOverflow;
      body.style.position = saved.position;
      body.style.top = saved.top;
      body.style.width = saved.width;
      body.style.overflow = saved.overflow;
      const behavior = html.style.scrollBehavior;
      html.style.scrollBehavior = 'auto';
      window.scrollTo(saved.x, saved.y);
      html.style.scrollBehavior = behavior;
      scrollState = null;
    }
    let closeTimer = null;
    let closing = false;
    const reduced = matchMedia('(prefers-reduced-motion:reduce)');
    function closePanel() {
      if (!dialog.open || closing) return;
      if (reduced.matches) { dialog.close(); return; }
      closing = true;
      dialog.classList.add('sn-closing');
      closeTimer = setTimeout(() => dialog.close(), 350);
    }
    listen(dialog, 'animationend', event => {
      if (event.target === scroller && ['sn-mobile-hide', 'sn-desktop-hide'].includes(event.animationName) && closing) dialog.close();
    });
    function openPanel(name, trigger) {
      if (closing || !findPanel(name)) return;
      if (desktop) desktop.close();
      if (dialog.open && !dialog.contains(trigger) && active === name) { closePanel(); return; }
      if (dialog.open) {
        if (active === 'navigation' && dialog.contains(trigger)) menuReturnFocus = trigger;
        setPanel(name);
      } else {
        scrollPositions.clear();
        menuReturnFocus = null;
        opener = trigger || document.activeElement;
        setPanel(name);
        lockScroll();
        dialog.showModal();
        updateTriggers();
      }
      focusPanel();
    }
    listen(dialog, 'close', () => {
      clearTimeout(closeTimer);
      closing = false;
      dialog.classList.remove('sn-closing');
      restoreScroll();
      active = '';
      activePanel = null;
      updateTriggers();
      if (opener?.isConnected && visible(opener)) opener.focus({ preventScroll: true });
      else root.querySelector('.sn-trigger[data-sn-open="menu"]')?.focus({ preventScroll: true });
      opener = null;
    });
    listen(dialog, 'cancel', event => { event.preventDefault(); closePanel(); });
    listen(dialog, 'keydown', event => {
      if (event.key !== 'Tab') return;
      const items = [...dialog.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),summary,[tabindex="0"]')].filter(visible);
      if (!items.length) { event.preventDefault(); return; }
      const first = items[0], last = items[items.length - 1], current = document.activeElement;
      if (event.shiftKey && (current === first || !items.includes(current))) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (current === last || !dialog.contains(current))) { event.preventDefault(); first.focus(); }
    });
    dialog.querySelectorAll('[data-sn-group]').forEach(group => listen(group, 'toggle', () => {
      if (group.open) dialog.querySelectorAll('[data-sn-group]').forEach(other => { if (other !== group) other.open = false; });
    }));
    let backdropDown = false;
    const outside = event => {
      const rect = dialog.getBoundingClientRect();
      return event.target === dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom);
    };
    listen(dialog, 'pointerdown', event => { backdropDown = outside(event); });
    listen(dialog, 'click', event => { if (backdropDown && outside(event)) closePanel(); backdropDown = false; });

    const rolloverTabs = [...dialog.querySelectorAll('[data-sn-rollover]')];
    const fine = matchMedia('(hover:hover) and (pointer:fine)');
    function showRollover(key) {
      if (!rolloverTabs.some(tab => tab.dataset.snRollover === key)) return;
      rolloverTabs.forEach(tab => { const on = tab.dataset.snRollover === key; tab.setAttribute('aria-selected', String(on)); tab.tabIndex = on ? 0 : -1; });
      dialog.querySelectorAll('[data-sn-rollover-panel]').forEach(panel => panel.hidden = panel.dataset.snRolloverPanel !== key);
      if (!mobile.matches && root.dataset.snDesign === 'b') {
        const image = dialog.querySelector(`[data-sn-rollover-panel="${key}"] [data-sn-atmosphere-image]`);
        if (image && !image.getAttribute('src')) {
          if (image.dataset.snAtmosphereSizes) image.sizes = image.dataset.snAtmosphereSizes;
          if (image.dataset.snAtmosphereSrcset) image.srcset = image.dataset.snAtmosphereSrcset;
          image.src = image.dataset.snAtmosphereImage;
        }
      }
    }
    let hoverTimer;
    rolloverTabs.forEach((tab, i) => {
      listen(tab, 'pointerenter', event => { if (!fine.matches || event.pointerType === 'touch') return; clearTimeout(hoverTimer); hoverTimer = setTimeout(() => showRollover(tab.dataset.snRollover), 110); });
      listen(tab, 'pointerleave', () => clearTimeout(hoverTimer));
      listen(tab, 'click', () => showRollover(tab.dataset.snRollover));
      listen(tab, 'focus', () => showRollover(tab.dataset.snRollover));
      listen(tab, 'keydown', event => {
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const j = event.key === 'Home' ? 0 : event.key === 'End' ? rolloverTabs.length - 1 : (i + (event.key === 'ArrowDown' ? 1 : -1) + rolloverTabs.length) % rolloverTabs.length;
        rolloverTabs[j].focus();
      });
    });
    dialog.querySelectorAll('.sn-panel-switch button[data-sn-open]').forEach(trigger => {
      if (!['spirits', 'experiences', 'recipes', 'trade'].includes(trigger.dataset.snOpen)) return;
      listen(trigger, 'pointerenter', event => { if (!fine.matches || event.pointerType === 'touch') return; clearTimeout(hoverTimer); hoverTimer = setTimeout(() => openPanel(trigger.dataset.snOpen, trigger), 110); });
      listen(trigger, 'pointerleave', () => clearTimeout(hoverTimer));
    });

    /* Desktop layers */
    const desktop = createDesktopMenus(root, dialog, listen, mobile, fine);

    const openFromKeyboard = (name, trigger) => {
      if (desktop.handles(name)) {
        if (desktop.open(name, trigger)) desktop.focusFirst();
        return;
      }
      openPanel(name, trigger);
      activePanel?.querySelector('button,a,input,summary')?.focus();
    };
    root.querySelectorAll('#sn-header .sn-nav-item').forEach(item => {
      const trigger = item.querySelector('[data-sn-open]');
      if (!trigger) return;
      listen(item, 'pointerenter', event => {
        if (!fine.matches || event.pointerType === 'touch') return;
        if (desktop.handles(trigger.dataset.snOpen)) { desktop.hover(trigger.dataset.snOpen, trigger); return; }
        clearTimeout(hoverTimer);
        hoverTimer = setTimeout(() => openPanel(trigger.dataset.snOpen, trigger), 180);
      });
      listen(item, 'pointerleave', () => { desktop.cancelHover(); clearTimeout(hoverTimer); });
      [item.querySelector('a'), trigger].forEach(el => listen(el, 'keydown', event => {
        if (!mobile.matches && event.key === 'ArrowDown') { event.preventDefault(); openFromKeyboard(trigger.dataset.snOpen, trigger); }
      }));
    });
    const locate = root.querySelector('#sn-header .sn-locate');
    if (locate) {
      listen(locate, 'pointerenter', event => { if (!fine.matches || event.pointerType === 'touch') return; if (desktop.handles('locations')) desktop.hover('locations', locate); });
      listen(locate, 'pointerleave', () => desktop.cancelHover());
      listen(locate, 'keydown', event => { if (event.key === 'ArrowDown' && desktop.handles('locations')) { event.preventDefault(); openFromKeyboard('locations', locate); } });
    }
    listen(root, 'click', event => {
      const trigger = event.target.closest('[data-sn-open]');
      if (trigger) {
        event.preventDefault();
        const name = trigger.dataset.snOpen;
        if (desktop.handles(name) && !dialog.contains(trigger)) {
          if (desktop.isOpen && trigger.getAttribute('aria-expanded') === 'true') desktop.close();
          else desktop.open(name, trigger);
          return;
        }
        openPanel(name, trigger);
        return;
      }
      if (event.target.closest('[data-sn-close]') && dialog.open) { closePanel(); return; }
      if (event.target.closest('[data-sn-back]') && dialog.open) { setPanel('menu', true); focusPanel(menuReturnFocus); }
    });
    tabs.forEach(tab => {
      listen(tab, 'click', () => { activeTab = tab.dataset.snTab; updateTabs(); });
      listen(tab, 'keydown', event => {
        if (!mobile.matches || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let position = tabs.indexOf(tab);
        if (event.key === 'Home') position = 0;
        else if (event.key === 'End') position = tabs.length - 1;
        else position = (position + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        activeTab = tabs[position].dataset.snTab;
        updateTabs();
        tabs[position].focus();
      });
    });
    function link(label, url, className) {
      let target;
      try { target = new URL(url, location.origin); } catch { return null; }
      if (!['https:', 'http:'].includes(target.protocol)) return null;
      const anchor = document.createElement('a');
      anchor.className = className;
      anchor.href = target.href;
      anchor.textContent = label;
      return anchor;
    }
    function renderSearch() {
      if (!input || !results) return;
      const query = input.value.trim();
      const needle = normalize(query);
      const terms = needle.split(/\s+/).filter(Boolean);
      if (clear) clear.hidden = !input.value;
      const matches = query
        ? index.filter(item => { const text = normalize(item.label + ' ' + (item.keywords || '') + ' ' + (item.group || '')); return terms.every(term => text.includes(term)); })
          .sort((a, b) => Number(normalize(b.label).startsWith(needle)) - Number(normalize(a.label).startsWith(needle))).slice(0, 8)
        : index.slice(0, 6);
      const groups = new Map();
      const fragment = document.createDocumentFragment();
      matches.forEach(item => { const group = item.group || 'Explore'; if (!groups.has(group)) groups.set(group, []); groups.get(group).push(item); });
      groups.forEach((items, group) => {
        const section = document.createElement('section');
        section.className = 'sn-result-group';
        const heading = document.createElement('h3');
        heading.className = 'sn-result-heading';
        heading.textContent = group;
        const list = document.createElement('ul');
        list.className = 'sn-result-list';
        items.forEach(item => { const anchor = link(item.label, item.url, 'sn-result-link'); if (anchor) { const row = document.createElement('li'); row.append(anchor); list.append(row); } });
        section.append(heading, list);
        fragment.append(section);
      });
      if (query) {
        const target = new URL(form?.getAttribute('action') || '/search', location.origin);
        target.searchParams.set('q', query);
        fragment.append(link('Search the whole site for “' + query + '”', target.href, 'sn-search-all'));
      }
      results.replaceChildren(fragment);
      if (status) status.textContent = query ? (matches.length ? matches.length + ' suggested ' + (matches.length === 1 ? 'destination.' : 'destinations.') : 'No suggested matches. Search the whole site below.') : 'Popular places to start.';
    }
    listen(input, 'input', renderSearch);
    listen(clear, 'click', event => { event.preventDefault(); input.value = ''; renderSearch(); input.focus({ preventScroll: true }); });
    listen(mobile, 'change', updateLayout);
    updateTabs();
    updateTriggers();
    renderSearch();
    root.stellarNavigationDestroy = () => {
      clearTimeout(closeTimer);
      clearTimeout(hoverTimer);
      desktop.destroy();
      if (dialog.open) dialog.close();
      restoreScroll();
      controller.abort();
      delete dialog.dataset.snReady;
    };
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', event => { event.target.querySelector('.stellar-navigation')?.stellarNavigationDestroy?.(); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
